import i18next from 'i18next';
import { Trash2Icon } from 'lucide-react';
import { Control, useFieldArray } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { ComboBoxResponsive } from '@/components/ui/combobox-responsive';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { IQuestionary } from '@/interfaces/IQuestionary';
import { UVCategory } from '@/interfaces/IUVCategory';

import { EMPTY_OPTIONS } from './helpers';

export type IPayloadTypeControl = Control<
  {
    payloads: { payload_type?: string | undefined; payload_count?: number | undefined }[];
  },
  any
>;

interface PayloadQuestionaryProps {
  questionary: IQuestionary;
  control: IPayloadTypeControl;
  enteredPayloads: { payload_type?: string | undefined }[] | undefined;
  selectedUVCategory: UVCategory | undefined;
  allowedPayloadCount?: number;
}

const PayloadQuestionary = ({
  questionary,
  control,
  enteredPayloads,
  selectedUVCategory,
  allowedPayloadCount = 4
}: PayloadQuestionaryProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'payloads',
    shouldUnregister: true
  });

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-2xl">{i18next.t('mission_report.section_payload')}</h1>
      {fields.map((field, index) => {
        const payloadTypeRequireCount = questionary.payload_type_v2?.find(
          item => item.value === enteredPayloads?.[index]?.payload_type
        )?.require_count;

        return (
          <div className="flex gap-2 flex-1" key={field.id}>
            <FormField
              control={control}
              name={`payloads.${index}.payload_type`}
              render={({ field }) => (
                <FormItem className="flex flex-col flex-1">
                  <FormLabel>
                    {i18next.t(
                      allowedPayloadCount === 1 ? `mission_report.payload` : `mission_report.payload_${index + 1}`
                    )}
                  </FormLabel>
                  <div className="flex gap-2 items-center">
                    <ComboBoxResponsive
                      drawerTitle={i18next.t('mission_report.payload')}
                      groups={[
                        {
                          options: (questionary.payload_type_v2 || EMPTY_OPTIONS).filter(
                            option =>
                              !selectedUVCategory ||
                              !option.vehicle_categories ||
                              option.vehicle_categories.includes(selectedUVCategory)
                          )
                        }
                      ]}
                      value={field.value}
                      onChange={field.onChange}
                      form
                      className="flex-1"
                    />
                    {index > 0 && !payloadTypeRequireCount && (
                      <Button type="button" size="lg" variant="outline" onClick={() => remove(index)}>
                        <Trash2Icon className="" />
                      </Button>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {payloadTypeRequireCount && (
              <div className="flex gap-2 flex-1 items-end">
                <FormField
                  control={control}
                  name={`payloads.${index}.payload_count`}
                  render={({ field }) => (
                    <FormItem className="flex flex-col flex-1">
                      <FormLabel>{i18next.t('mission_report.payload_count')}</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder={i18next.t('placeholders.number')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {index > 0 && (
                  <Button type="button" size="lg" variant="outline" onClick={() => remove(index)}>
                    <Trash2Icon className="" />
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {fields.length < allowedPayloadCount && (
        <Button
          size="lg"
          type="button"
          variant="secondary"
          onClick={() => append({ payload_type: '', payload_count: 1 })}
        >
          {i18next.t('controls.add')}
        </Button>
      )}
    </div>
  );
};

export default PayloadQuestionary;
